/* eslint-disable @angular-eslint/component-class-suffix */
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {ToolsBoxService} from '../../Services/tools.service';
import {UserService} from '../../Services/user.service';
import {User} from '../../Models/user.model';
import {SearchService} from '../../Services/search.service';
import {NotificationService} from "../../Services/notification.service";
import {catchError, tap} from "rxjs/operators";
import {of} from "rxjs";
import { SearchBarComponent } from '../Shared/search-bar/search-bar.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UserInformationTabComponent } from './tabs/user-information-tab/user-information-tab.component';
import { FilterPipe } from '../../Pipes/filter.pipe';
import { TrigrammePipe } from '../../Pipes/trigramme.pipe';
import { FirstLetterPipe } from '../../Pipes/firstletter.pipe';
import { NgClass } from '@angular/common';

@Component({
    selector: 'usersAdministration',
    templateUrl: 'usersAdministration.component.html',
    styleUrls: ['usersAdministration.component.css'],
    imports: [SearchBarComponent, ReactiveFormsModule, FormsModule, RouterLink, UserInformationTabComponent, FilterPipe, TrigrammePipe, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class UsersAdministration implements OnInit {

  showResetModal = false; // Flag to show/hide the reset modal
  isInfoPanelOpen = true;
  showCreateModal = false;

  // Itz list displayed
  $itzList: User[] = [];
  displayInactiveUser = false; // Flag to show/hide inactive users

  // Data that will be edited
  selectedUser: User = new User();

  // Filter
  itzFilter = "";

  private readonly toolsBoxService: ToolsBoxService = inject(ToolsBoxService);
  private readonly userService: UserService = inject(UserService);
  private readonly router: Router = inject(Router);
  private readonly searchService: SearchService = inject(SearchService);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    this.toolsBoxService.changeHeaderName("Users Administration");
    this.initItzList();
  }

  // Initialize the Itz list
  initItzList = () => {
    this.toolsBoxService.tellActionIsInProgress(true);
    this.userService.getItzList().subscribe({
      next: (list: User[]) => {
        this.toolsBoxService.tellActionIsInProgress(false);
        this.$itzList = list;
        this.cdr.markForCheck();
      },
      error: err => {
        this.toolsBoxService.tellActionIsInProgress(false);
        this.router.navigate(["Error", {errMessage: err.error}]);
      }
    });
  }

  // Initialize the input form with user information
  initInformationInputForm = (itz?: User) => {
    this.selectedUser = {...itz};
    this.isInfoPanelOpen = true;
    this.showCreateModal = true;
  }

  getStyle(itz: User): object {
    const firstLetterPipe = new FirstLetterPipe();
    return {
      'avatar-icon': true,
      ['user-first-initial-' + firstLetterPipe.transform(itz.userName)]: true
    };
  }

  // Handle user updates from the child component
  onUserUpdated(updatedUser: User) {
    this.userService.createITz(updatedUser).pipe(
      tap(message => {
        this.notificationService.setType("info");
        this.notificationService.setMessage(message);
        this.selectedUser = updatedUser;
        const index = this.$itzList.findIndex(u => u.id === updatedUser.id);
        if (index !== -1) {
          this.$itzList[index] = updatedUser;
          this.$itzList = [...this.$itzList];
          this.cdr.markForCheck();
        } else {
          // New user created: reload the full list to get server-assigned ID
          this.initItzList();
        }
      }),
      catchError(err => {
        this.notificationService.setType("error");
        this.notificationService.setMessage(err?.message || "Unknown error");
        return of(null);
      })).subscribe();

  }

  // Reset the filter
  resetFilter = (): void => {
    this.itzFilter = "";
    this.search();
  }

  shouldHideUser(itz: User): boolean {
    if (this.displayInactiveUser) {
      return false;
    } else {
      return itz.userStatus === 'INACTIVE';
    }
  }

  search = (): void => {
    this.$itzList = this.searchService.search(this.$itzList, this.itzFilter);
    this.cdr.markForCheck();
  }


}
